/**
 * @file Developer debug overlay for the suicide-bomber AI (issue #5).
 *
 * Hidden during normal play; the developer window (`*`) switches it on. When
 * on, it draws — for every entity carrying an {@link ../components/ExplosiveTrigger.js}
 * — its detection cone, view direction, line of sight to the player, current
 * navigation destination, explosion radius and trigger-distance rings, plus a
 * text panel listing each bomber's live state, target, speed, distance and fuse.
 *
 * It only *reads* components and pushes throwaway line geometry into the scene,
 * so it can be registered unconditionally: when disabled it costs one boolean
 * test per frame, and in production the window that owns the switch is absent.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import { Transform, Velocity, AIBrain, AIState, ExplosiveTrigger, PlayerControlled, Health } from '../components/index.js';
import { PALETTE } from '../config/Palette.js';

/** Segments used to approximate a debug circle. */
const CIRCLE_SEGMENTS = 40;
/** Height the debug lines float above the floor, so they read from above. */
const DEBUG_Y = 0.15;

export class DebugOverlaySystem extends System {
  // After RenderSystem so the group is present for the frame's draw.
  static priority = 110;

  constructor() {
    super();
    /** @type {boolean} Whether the overlay is currently shown. @private */
    this._debugVisible = false;
    /** @type {THREE.Group | null} Container for all debug lines. @private */
    this._group = null;
    /** @type {THREE.LineBasicMaterial[]} Pooled materials by colour. @private */
    this._materials = new Map();
    /** @type {HTMLDivElement | null} Text panel. @private */
    this._panel = null;
  }

  /** @returns {void} */
  init() {
    this._group = new THREE.Group();
    this._group.visible = false;
    this._group.renderOrder = 999;
    this.ctx.scene.add(this._group);
  }

  /**
   * @param {number} _dt Delta time in seconds.
   * @returns {void}
   */
  update(_dt) {
    if (!this._debugVisible) return;

    this._clear();

    const player = this._findPlayer();
    const bombers = this.world.query(AIBrain, Transform, ExplosiveTrigger);
    /** @type {string[]} */
    const lines = [];

    for (const entity of bombers) {
      if (!entity.alive) continue;
      const brain = entity.get(AIBrain);
      const trigger = entity.get(ExplosiveTrigger);
      const transform = entity.get(Transform);
      this._drawBomber(entity, brain, trigger, transform, player);
      lines.push(this._describe(entity, brain, trigger, transform, player));
    }

    this._renderPanel(lines);
  }

  /**
   * True while the overlay is drawing. Deliberately not `enabled`: that name
   * belongs to {@link ../ecs/System.js System}, which uses it to skip a system
   * altogether.
   * @returns {boolean}
   */
  get debugVisible() {
    return this._debugVisible;
  }

  /**
   * Shows or hides the overlay. The debug window owns the switch — this system
   * no longer listens for a key of its own (issue #56).
   *
   * @param {boolean} visible Desired state.
   * @returns {void}
   */
  setDebugVisible(visible) {
    this._debugVisible = Boolean(visible);
    if (this._group) this._group.visible = this._debugVisible;
    if (!this._debugVisible) {
      this._clear();
      this._renderPanel(null);
    }
  }

  /**
   * Draws every debug primitive for one bomber.
   * @param {import('../ecs/Entity.js').Entity} entity Bomber.
   * @param {import('../components/AIBrain.js').AIBrain} brain Its brain.
   * @param {import('../components/ExplosiveTrigger.js').ExplosiveTrigger} trigger Its blast.
   * @param {import('../components/Transform.js').Transform} transform Its transform.
   * @param {import('../ecs/Entity.js').Entity | null} player The live player.
   * @returns {void}
   * @private
   */
  _drawBomber(entity, brain, trigger, transform, player) {
    const { x, z } = transform.position;
    const isAggro = brain.state === AIState.AGGRO_CHARGE || brain.state === AIState.EXPLODING;

    // Detection cone: apex + arc, coloured by whether it is hunting.
    if (brain.detectionConeRange > 0) {
      this._cone(x, z, transform.rotationY, brain.detectionConeHalfAngle, brain.detectionConeRange, isAggro ? PALETTE.telegraph : PALETTE.enemySpitter);
    }

    // View direction.
    const fwd = 2;
    this._line([x, x + Math.sin(transform.rotationY) * fwd], [z, z + Math.cos(transform.rotationY) * fwd], PALETTE.playerCore);

    // Explosion radius and trigger-distance rings.
    this._ring(x, z, trigger.radius, PALETTE.explosion);
    this._ring(x, z, trigger.triggerDistance, PALETTE.enemySuicideBomber);

    // Navigation destination.
    if (brain.hasNavTarget) {
      this._ring(brain.navTargetX, brain.navTargetZ, 0.4, PALETTE.pickupSpeed);
      this._line([x, brain.navTargetX], [z, brain.navTargetZ], PALETTE.pickupSpeed);
    }

    // Line of sight to the player.
    if (player) {
      const p = player.get(Transform).position;
      const clear = brain.hasLineOfSight || brain.state === AIState.AGGRO_CHARGE;
      this._line([x, p.x], [z, p.z], clear ? PALETTE.pickupHealth : PALETTE.enemyGrunt);
    }
  }

  /**
   * Builds a one-line status string for the text panel.
   * @param {import('../ecs/Entity.js').Entity} entity Bomber.
   * @param {import('../components/AIBrain.js').AIBrain} brain Its brain.
   * @param {import('../components/ExplosiveTrigger.js').ExplosiveTrigger} trigger Its blast.
   * @param {import('../components/Transform.js').Transform} transform Its transform.
   * @param {import('../ecs/Entity.js').Entity | null} player The live player.
   * @returns {string}
   * @private
   */
  _describe(entity, brain, trigger, transform, player) {
    const dist = player
      ? Math.hypot(player.get(Transform).position.x - transform.position.x, player.get(Transform).position.z - transform.position.z)
      : Infinity;
    const velocity = entity.get(Velocity);
    const speed = velocity ? Math.hypot(velocity.linear.x, velocity.linear.z) : 0;
    return (
      `#${entity.id} ${brain.state}` +
      ` | tgt:${brain.target ? 'y' : 'n'}` +
      ` dist:${Number.isFinite(dist) ? dist.toFixed(1) : '--'}` +
      ` spd:${speed.toFixed(1)}` +
      ` nav:${brain.hasNavTarget ? 'y' : 'n'}` +
      ` stuck:${brain.isStuck ? 'y' : 'n'}` +
      ` fuse:${trigger.hasStartedExploding ? trigger.fuseRemaining.toFixed(2) : '--'}` +
      ` boom:${trigger.hasAppliedDamage ? 'y' : 'n'}`
    );
  }

  // ----------------------------------------------------------- draw helpers

  /**
   * @param {number} cx Centre X.
   * @param {number} cz Centre Z.
   * @param {number} facing Heading in radians.
   * @param {number} halfAngle Cone half-angle in radians.
   * @param {number} range Cone reach.
   * @param {number} color Line colour.
   * @returns {void}
   * @private
   */
  _cone(cx, cz, facing, halfAngle, range, color) {
    const xs = [cx];
    const zs = [cz];
    const steps = 20;
    for (let i = 0; i <= steps; i++) {
      const a = facing - halfAngle + (2 * halfAngle * i) / steps;
      xs.push(cx + Math.sin(a) * range);
      zs.push(cz + Math.cos(a) * range);
    }
    xs.push(cx);
    zs.push(cz);
    this._line(xs, zs, color);
  }

  /**
   * @param {number} cx Centre X.
   * @param {number} cz Centre Z.
   * @param {number} radius Ring radius.
   * @param {number} color Line colour.
   * @returns {void}
   * @private
   */
  _ring(cx, cz, radius, color) {
    if (radius <= 0) return;
    const xs = [];
    const zs = [];
    for (let i = 0; i <= CIRCLE_SEGMENTS; i++) {
      const a = (TAU_LOCAL * i) / CIRCLE_SEGMENTS;
      xs.push(cx + Math.cos(a) * radius);
      zs.push(cz + Math.sin(a) * radius);
    }
    this._line(xs, zs, color);
  }

  /**
   * Adds a poly-line at the debug height from parallel x/z arrays.
   * @param {number[]} xs X coordinates.
   * @param {number[]} zs Z coordinates.
   * @param {number} color Line colour.
   * @returns {void}
   * @private
   */
  _line(xs, zs, color) {
    const positions = new Float32Array(xs.length * 3);
    for (let i = 0; i < xs.length; i++) {
      positions[i * 3] = xs[i];
      positions[i * 3 + 1] = DEBUG_Y;
      positions[i * 3 + 2] = zs[i];
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const line = new THREE.Line(geometry, this._material(color));
    line.frustumCulled = false;
    this._group.add(line);
  }

  /**
   * @param {number} color Colour key.
   * @returns {THREE.LineBasicMaterial}
   * @private
   */
  _material(color) {
    let material = this._materials.get(color);
    if (!material) {
      material = new THREE.LineBasicMaterial({ color, toneMapped: false, transparent: true, opacity: 0.85 });
      this._materials.set(color, material);
    }
    return material;
  }

  /**
   * Removes and disposes every line from the previous frame.
   * @returns {void}
   * @private
   */
  _clear() {
    if (!this._group) return;
    for (let i = this._group.children.length - 1; i >= 0; i--) {
      const child = this._group.children[i];
      this._group.remove(child);
      child.geometry?.dispose();
    }
  }

  // ------------------------------------------------------------- text panel

  /**
   * @param {string[] | null} lines Panel rows, or null to hide it.
   * @returns {void}
   * @private
   */
  _renderPanel(lines) {
    if (!lines || lines.length === 0) {
      if (this._panel) this._panel.style.display = 'none';
      return;
    }
    if (!this._panel) this._panel = this._createPanel();
    this._panel.style.display = 'block';
    this._panel.textContent = `AI DEBUG (*)  bombers: ${lines.length}\n${lines.join('\n')}`;
  }

  /**
   * @returns {HTMLDivElement}
   * @private
   */
  _createPanel() {
    const panel = document.createElement('div');
    Object.assign(panel.style, {
      position: 'fixed',
      left: '8px',
      bottom: '8px',
      maxWidth: '46ch',
      maxHeight: '40vh',
      overflow: 'hidden',
      padding: '6px 8px',
      font: '11px/1.35 monospace',
      whiteSpace: 'pre',
      color: '#d6faff',
      background: 'rgba(4, 6, 13, 0.78)',
      border: '1px solid #11d9ff',
      borderRadius: '4px',
      pointerEvents: 'none',
      zIndex: '9999'
    });
    document.body.appendChild(panel);
    return panel;
  }

  /**
   * @returns {import('../ecs/Entity.js').Entity | null}
   * @private
   */
  _findPlayer() {
    for (const player of this.world.query(PlayerControlled, Transform, Health)) {
      if (!player.get(Health).dead) return player;
    }
    return null;
  }

  /** @returns {void} */
  dispose() {
    this._clear();
    if (this._group) {
      this.ctx.scene.remove(this._group);
      this._group = null;
    }
    for (const material of this._materials.values()) material.dispose();
    this._materials.clear();
    if (this._panel) {
      this._panel.remove();
      this._panel = null;
    }
  }
}

/** Local TAU to avoid an import cycle through MathUtils in a debug-only file. */
const TAU_LOCAL = Math.PI * 2;
